import React, { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

import { AnchorLink } from "gatsby-plugin-anchor-links";

import Variation from "./variation";
import BundleSelector from "./bundleSelector";
import AddToCartButton from "./addToCartButton";
import SizeChart from "./sizeChart";

import PrimaryButton from "../shared/primaryButton";
import QuantitySelector from "../shared/quantitySelector";

import { __t } from "../../utils/translate";
import { Product } from "../../utils/types";

import StockTimer from "@/components/product/stockTimer";
import { useProductContext } from "@/providers/productProvider";

type ProductAddToCartProps = {
    product: Product;
    className: string;
    attributesBundle: { products: Record<string, Product>; };
    setAttributeBundle: (attribute: any) => void;
    isCoupon: boolean;
    prices: Record<string, number>;
};

const ProductAddToCart: React.FC<ProductAddToCartProps> = ({
    product,
    attributesBundle,
    setAttributeBundle,
    isCoupon,
    prices
}) => {
    const { quantity, setQuantity, currency_symbol, attributes, variationId, setAttribute, isOutOfStock } = useProductContext();

    const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);

    const openSizeChart = () => {
        setIsSizeChartOpen(true);
    };

    const closeSizeChart = () => {
        setIsSizeChartOpen(false);
    };

    const setVariation = (id: any, attributes: any) => {
        setAttribute(attributes);
    }

    const renderPrice = () => {
        return (
            <>
                {!isCoupon &&
                    <div className={`${isOutOfStock ? 'opacity-0' : ''} flex flex-row gap-3 items-end text-title text-[21px] md:text-h3 md:mt-1 whitespace-nowrap`}>
                        {!!prices.sale_price && (
                            <>
                                <div className={`product_regular_price font-semibold md:line-through linetrough text-red`}>
                                    <div className="inline"
                                        dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                    />
                                    {prices.regular_price}
                                </div>
                                <div className={`product_sale_price font-semibold text-button-accent-color`}>
                                    <div className="inline"
                                        dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                    />
                                    {prices.sale_price}
                                </div>
                            </>
                        ) || (
                                <div className={`product_price font-semibold text-button-accent-color`}>
                                    <div className="inline"
                                        dangerouslySetInnerHTML={{ __html: currency_symbol }}
                                    />
                                    {prices.regular_price}
                                </div>
                            )}
                    </div>
                }
                {isOutOfStock ?
                    <p className={`text-base-16-strong md:text-title pb-2 md:pb-4 text-red`}>
                        {__t('Out of stock')}
                    </p> :
                    <StockTimer productId={product.wordpress_id} />
                }
            </>
        )
    };

    return (
        <>
            <div>
                {product.stock_status === "instock" && (
                    <>
                        {Object.keys(attributesBundle).length > 1 ? (
                            <>
                                {renderPrice()}
                                <BundleSelector
                                    bundle={attributesBundle}
                                    attributes={product.attributes}
                                    isCoupon={isCoupon}
                                    setAttributeBundle={setAttributeBundle}
                                />
                            </>
                        ) : (
                            <>
                                <Variation
                                    product={product}
                                    isCoupon={isCoupon}
                                    setVariation={setVariation}
                                    defaultAttributes={attributes}
                                />
                                {renderPrice()}
                            </>
                        )}

                        {(!!product.acf.measure?.header || !!product.acf.sizeguide?.image || !!product.acf.size_guide?.url || !!product.acf.size_eyewear?.url) && (
                            <div className={`${isCoupon ? 'mb-2' : 'mb-3'} mt-2 md:mt-1 pt-3 sm:border-0 flex flex-row items-center md:items-start md:justify-start gap-2`}>
                                <p className="text-small-14 md:text-18 text-main-text-color">
                                    {__t("Be sure to check the size in our")}
                                </p>
                                <a
                                    onClick={openSizeChart}
                                    href="#"
                                    className={`text-h3-r lg:text-h3 underline underline-offset-2 text-[#0000ff] hover:text-dark-blu`}
                                >
                                    {__t("Size Guide")}
                                </a>
                            </div>
                        ) || product.acf.sizeguide?.anchor && (
                            <div className={`${isCoupon ? 'mb-2' : 'mb-3'} mt-1 pt-3 flex flex-row items-start md:items-start md:justify-start gap-2`}>
                                <p className="text-small-14 md:text-18 text-main-text-color">
                                    {__t("Be sure to check the size in our")}:
                                </p>
                                <AnchorLink to={product.acf.sizeguide.anchor} className="text-h3-r lg:text-h3 underline underline-offset-2 text-[#0000ff] hover:text-dark-blu" title={__t("Size Guide")} >
                                    {__t("Size Guide")}
                                </AnchorLink>
                            </div>
                        )}

                        <div className={`${isCoupon ? 'hidden' : ''} flex flex-row items-end gap-2 mb-3 mt-[32px]`}>
                            <QuantitySelector
                                className={`${isOutOfStock ? 'opacity-50 cursor-no-drop' : ''}!text-[17px] h-[58px] pl-6 w-[50px]`}
                                buttonStyle='right-0 top-0 !px-1 justify-around min-w-[33px]'
                                boxWidth=""
                                symbolHeight="21"
                                quantity={quantity}
                                setQuantity={setQuantity}
                            />
                            <AddToCartButton
                                className="h-[50px] single_add_to_cart_button rounded-md w-full"
                                classNameWrapper="min-w-[200px]"
                                product={product}
                                variationId={variationId}
                                productName={product.name}
                                productPrice={parseInt(product.price)}
                                quantity={quantity}
                                bundle={attributesBundle}
                                cardAnimation={true}
                            />
                        </div>
                    </>
                ) || product.stock_status === "discontinued" && (
                    <div className="flex flex-col my-8">
                        <div className="flex flex-row items-center justify-between mb-6">
                            <span className="text-base-16-strong md:text-title text-red">
                                {__t('This product has been discontinued.')}
                            </span>
                            <svg className="h-6 w-6 p-[5px]" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M10.5303 1.53033C10.8232 1.23744 10.8232 0.762563 10.5303 0.46967C10.2374 0.176777 9.76256 0.176777 9.46967 0.46967L5.5 4.43934L1.53033 0.46967C1.23744 0.176777 0.762563 0.176777 0.46967 0.46967C0.176777 0.762563 0.176777 1.23744 0.46967 1.53033L4.43934 5.5L0.46967 9.46967C0.176777 9.76256 0.176777 10.2374 0.46967 10.5303C0.762563 10.8232 1.23744 10.8232 1.53033 10.5303L5.5 6.56066L9.46967 10.5303C9.76256 10.8232 10.2374 10.8232 10.5303 10.5303C10.8232 10.2374 10.8232 9.76256 10.5303 9.46967L6.56066 5.5L10.5303 1.53033Z" fill="#FF0000" />
                            </svg>
                        </div>
                    </div>
                ) || (
                        <div className="flex flex-col my-8">
                            <div className="flex flex-row items-center justify-between mb-6">
                                <span className="text-base-16-strong md:text-title text-red">
                                    {__t("This item is now sold out and unavailable")}
                                </span>
                                <svg className="h-6 w-6 p-[5px]" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 11 11" fill="none">
                                    <path fillRule="evenodd" clipRule="evenodd" d="M10.5303 1.53033C10.8232 1.23744 10.8232 0.762563 10.5303 0.46967C10.2374 0.176777 9.76256 0.176777 9.46967 0.46967L5.5 4.43934L1.53033 0.46967C1.23744 0.176777 0.762563 0.176777 0.46967 0.46967C0.176777 0.762563 0.176777 1.23744 0.46967 1.53033L4.43934 5.5L0.46967 9.46967C0.176777 9.76256 0.176777 10.2374 0.46967 10.5303C0.762563 10.8232 1.23744 10.8232 1.53033 10.5303L5.5 6.56066L9.46967 10.5303C9.76256 10.8232 10.2374 10.8232 10.5303 10.5303C10.8232 10.2374 10.8232 9.76256 10.5303 9.46967L6.56066 5.5L10.5303 1.53033Z" fill="#FF0000" />
                                </svg>
                            </div>
                            <div className="w-full flex flex-col justify-center items-center border border-stroke-color">
                                <p className="text-base-16 lg:text-h3-r text-white text-center px-2 py-4 md:py-5 bg-dark-blu w-full">
                                    {__t("Email me when available")}
                                </p>
                                <div className="flex flex-col gap-4 px-5 py-8 justify-center w-full">
                                    <input
                                        className="cursor-input w-full border border-stroke-color py-[10px] px-4 text-base-16 text-footer-text-color focus:placeholder:opacity-0"
                                        type="email"
                                        id="email-input"
                                        value=""
                                        placeholder={__t("Your Email")}
                                    />
                                    <PrimaryButton
                                        className="h-[44px] cursor-pointer"
                                        textStyle="!text-base-16-strong !xl:text-h3-r"
                                        onClick={undefined}>
                                        {__t("Send")}
                                    </PrimaryButton>
                                </div>
                            </div>
                        </div>
                    )}
            </div>

            {isSizeChartOpen && (
                <div onClick={closeSizeChart} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
                    <div className="relative bg-white h-90vh max-h-[90vh] overflow-auto p-2">
                        <button
                            onClick={closeSizeChart}
                            className="absolute top-4 right-4 cursor-pointer"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path fillRule="evenodd" clipRule="evenodd" d="M13.7071 1.70711C14.0976 1.31658 14.0976 0.683417 13.7071 0.292893C13.3166 -0.0976311 12.6834 -0.0976311 12.2929 0.292893L7 5.58579L1.70711 0.292893C1.31658 -0.0976311 0.683417 -0.0976311 0.292893 0.292893C-0.0976311 0.683417 -0.0976311 1.31658 0.292893 1.70711L5.58579 7L0.292893 12.2929C-0.0976311 12.6834 -0.0976311 13.3166 0.292893 13.7071C0.683417 14.0976 1.31658 14.0976 1.70711 13.7071L7 8.41421L12.2929 13.7071C12.6834 14.0976 13.3166 14.0976 13.7071 13.7071C14.0976 13.3166 14.0976 12.6834 13.7071 12.2929L8.41421 7L13.7071 1.70711Z" fill="#405465" />
                            </svg>
                        </button>
                        <SizeChart
                            className="py-4 md:px-16 max-w-full w-[768px]"
                            product={product}
                        />
                    </div>
                </div>
            )}
        </>
    )
}

export default ProductAddToCart;